if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends HTMLElement {
      static CEP_KEY = 'shipping-calculator:cep';
      static WEBHOOK_URL = 'https://gandini-n8n.lui.cx/webhook/product/shipping-rates';

      constructor() {
        super();

        this.input = this.querySelector('[data-shipping-input]');
        this.button = this.querySelector('[data-shipping-submit]');
        this.buttonText = this.querySelector('.shipping-calculator__button-text');
        this.spinner = this.querySelector('.shipping-calculator__spinner');
        this.statusEl = this.querySelector('[data-shipping-status]');
        this.statusTextEl = this.querySelector('[data-shipping-status-text]');
        this.resultsEl = this.querySelector('[data-shipping-results]');

        this.productFormId = this.dataset.productFormId;
        this.currency = this.dataset.currency || 'BRL';
        this.loading = false;

        this.onInput = this.onInput.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.calculate = this.calculate.bind(this);
      }

      connectedCallback() {
        if (this.input) {
          this.input.addEventListener('input', this.onInput);
          this.input.addEventListener('keydown', this.onKeydown);
        }
        if (this.button) {
          this.button.addEventListener('click', this.calculate);
        }
        this.restoreFromStorage();
      }

      restoreFromStorage() {
        const savedCep = this.storageGet(ShippingCalculator.CEP_KEY);
        if (!savedCep || !this.input) return;
        this.input.value = savedCep.length > 5 ? `${savedCep.slice(0, 5)}-${savedCep.slice(5)}` : savedCep;
        if (savedCep.length === 8) this.calculate();
      }

      storageGet(key) {
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          return null;
        }
      }

      storageSet(key, value) {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          /* localStorage unavailable */
        }
      }

      onInput() {
        const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
        this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
        if (digits.length === 8) this.calculate();
      }

      onKeydown(event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.calculate();
        }
      }

      getDigits() {
        return this.input.value.replace(/\D/g, '');
      }

      getVariantId() {
        let id = this.dataset.variantId;
        const form = this.productFormId ? document.getElementById(this.productFormId) : null;
        if (form) {
          const variantInput = form.querySelector('[name="id"]');
          if (variantInput && variantInput.value) id = variantInput.value;
        }
        return id;
      }

      setLoading(state) {
        this.loading = state;
        if (this.button) this.button.disabled = state;
        if (this.buttonText) this.buttonText.toggleAttribute('hidden', state);
        if (this.spinner) this.spinner.toggleAttribute('hidden', !state);
      }

      setStatus(message) {
        if (!this.statusEl) return;
        if (this.statusTextEl) this.statusTextEl.textContent = message || '';
        this.statusEl.toggleAttribute('hidden', !message);
      }

      clearOutput() {
        this.setStatus('');
        if (this.resultsEl) this.resultsEl.innerHTML = '';
      }

      formatMoney(value) {
        const amount = parseFloat(value);
        if (Number.isNaN(amount)) return value;
        try {
          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: this.currency }).format(amount);
        } catch (error) {
          return `R$ ${amount.toFixed(2).replace('.', ',')}`;
        }
      }

      renderRates(rates) {
        if (!this.resultsEl) return;
        this.resultsEl.innerHTML = '';

        if (!rates || rates.length === 0) {
          this.setStatus('Nenhuma opção de frete disponível para este CEP.');
          return;
        }

        rates.forEach((rate) => {
          const li = document.createElement('li');
          li.className = 'shipping-calculator__result';

          const info = document.createElement('div');
          info.className = 'shipping-calculator__result-info';

          const name = document.createElement('span');
          name.className = 'shipping-calculator__result-name';
          name.textContent = rate.service_name || rate.presentment_name || rate.name;
          name.textContent =
            name.textContent + (rate.carrier && name.textContent !== rate.carrier ? ' - ' + rate.carrier : '');
          info.appendChild(name);

          const deadline = this.getDeadlineLabel(rate);
          if (deadline) {
            const term = document.createElement('span');
            term.className = 'shipping-calculator__result-term caption';
            term.textContent = ' ⇢ ' + deadline;
            info.appendChild(term);
          }

          const price = document.createElement('span');
          price.className = 'shipping-calculator__result-price';
          price.textContent = parseFloat(rate.price) === 0 ? 'Grátis' : this.formatMoney(rate.price);

          li.appendChild(info);
          li.appendChild(price);
          this.resultsEl.appendChild(li);
        });
      }

      updateEtaSummary(rates, cep) {
        const etaElements = document.querySelectorAll('[data-shipping-eta]');
        if (etaElements.length === 0) return;

        if (!rates || rates.length === 0) {
          etaElements.forEach((el) => el.setAttribute('hidden', ''));
          return;
        }

        const days = rates.map((rate) => parseInt(rate.delivery_time, 10)).filter((value) => !Number.isNaN(value));
        const prices = rates.map((rate) => parseFloat(rate.price)).filter((value) => !Number.isNaN(value));

        const parts = [];
        if (days.length > 0) {
          const minDays = Math.min(...days);
          parts.push(`Receba em até ${minDays} ${minDays === 1 ? 'dia útil' : 'dias úteis'}`);
        }
        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          parts.push(minPrice === 0 ? 'frete grátis' : `frete a partir de ${this.formatMoney(minPrice)}`);
        }
        if (parts.length === 0) {
          etaElements.forEach((el) => el.setAttribute('hidden', ''));
          return;
        }

        const formattedCep = cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
        const summary = `${parts.join(' · ')} para o CEP ${formattedCep}`;

        etaElements.forEach((el) => {
          const textEl = el.querySelector('[data-shipping-eta-text]') || el;
          textEl.textContent = summary;
          el.removeAttribute('hidden');
        });
      }

      getDeadlineLabel(rate) {
        const days = parseInt(rate.delivery_time, 10);
        if (!Number.isNaN(days)) {
          return `${days} ${days === 1 ? 'dia útil' : 'dias úteis'}`;
        }
        if (rate.delivery_label) return `${rate.delivery_label}`;
        return '';
      }

      async fetchRates(cep) {
        const payload = {
          productVariantId: String(this.getVariantId()),
          quantity: 1,
          zipcode: cep,
        };

        const response = await fetch(ShippingCalculator.WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`Webhook request failed with status ${response.status}`);

        const data = await response.json();
        if (Array.isArray(data)) return data;
        return data.services || data.shipping_rates || [];
      }

      async calculate() {
        if (this.loading) return;

        const cep = this.getDigits();
        if (cep.length !== 8) {
          this.clearOutput();
          this.setStatus('Digite um CEP válido com 8 dígitos.');
          return;
        }

        this.setLoading(true);
        this.clearOutput();
        this.storageSet(ShippingCalculator.CEP_KEY, cep);

        try {
          const rates = await this.fetchRates(cep);
          this.renderRates(rates);
          this.updateEtaSummary(rates, cep);
        } catch (error) {
          console.error('[shipping-calculator]', error);
          this.setStatus('Não foi possível calcular o frete agora. Tente novamente em instantes.');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
